import { useMemo, memo } from 'react';
import LeafletMap, { StateBoundary, LocationMarker } from '@/components/Map';

function EventsMap({ events, selectedStates = [], className = '' }) {
  // Filter events with valid coordinates and group by location
  const eventsByLocation = useMemo(() => {
    const locationMap = {};

    events.forEach((event) => {
      const lat = parseFloat(event.latitude);
      const lng = parseFloat(event.longitude);

      if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
        // Create a location key (rounded to 3 decimal places for grouping nearby events)
        const locationKey = `${lat.toFixed(3)},${lng.toFixed(3)}`;

        if (!locationMap[locationKey]) {
          locationMap[locationKey] = {
            lat,
            lng,
            events: [],
            locationKey,
          };
        }
        locationMap[locationKey].events.push(event);
      }
    });

    return Object.values(locationMap);
  }, [events]);

  // Calculate map bounds from all event locations
  // Return null when no events - Map will use default center/zoom
  const mapBounds = useMemo(() => {
    if (eventsByLocation.length === 0) return null;

    return eventsByLocation.map(({ lat, lng }) => [lat, lng]);
  }, [eventsByLocation]);

  // Keep map mounted even with no events to prevent flash on filter changes
  return (
    <div
      className={`h-[400px] rounded overflow-hidden border border-brown-100/10 ${className}`}
    >
      <LeafletMap
        bounds={mapBounds}
        style={{ height: '100%', borderRadius: '4px' }}
      >
        {/* State boundary outline when filtering by state */}
        {selectedStates.length > 0 && (
          <StateBoundary selectedStates={selectedStates} />
        )}
        {eventsByLocation.map(
          ({ lat, lng, events: locationEvents, locationKey }) => (
            <LocationMarker
              key={locationKey}
              lat={lat}
              lng={lng}
              events={locationEvents}
            />
          )
        )}
      </LeafletMap>
    </div>
  );
}

export default memo(EventsMap);
